import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import { InquisitiveEmeji } from 'global/graphql/scalars.type';
import { Dropdown, DropdownIndicator } from 'approot/shared/dropdown/dropdown';
import {
  DropdownItem,
  DropdownItemLabel,
} from 'approot/shared/dropdown/dropdown-item.style';
import { getStudentDisplayName } from 'approot/students/logic/get-student-display-name';
import ClassroomStudentIdentifier from './classroom-student-identifier';
import * as Styled from './classroom-student-answer-dropdown.style';

type Props = {
  selectedStudentId: number;
  students: {
    id: number;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    emeji: InquisitiveEmeji | null;
  }[];
  disabled?: boolean;
  includeAllOption?: boolean;
  isSmall?: boolean;
  onSelect: (id: number) => void;
};

const ClassRoomStudentAnswerDropdown = ({
  students,
  selectedStudentId,
  disabled = false,
  includeAllOption = true,
  isSmall = false,
  onSelect,
}: Props) => {
  const sortedStudents = useMemo(() => {
    return sortBy(
      students.map(student => ({
        id: student.id,
        name: getStudentDisplayName(student),
        emoji: student.emeji,
      })),
      student => student.name.toLowerCase()
    );
  }, [students]);

  return (
    <Dropdown
      disabled={disabled}
      dropdownButtonContents={
        <Styled.ClassRoomStudentAnswerDropdown $isSmall={isSmall}>
          {includeAllOption && selectedStudentId === 0 ? (
            <span>All Students ({sortedStudents.length})</span>
          ) : !includeAllOption && selectedStudentId === 0 ? (
            <span>Select a Student</span>
          ) : (
            <ClassroomStudentIdentifier
              size={isSmall ? 24 : 40}
              name={
                sortedStudents.find(s => s.id === selectedStudentId)?.name || ''
              }
              emoji={
                sortedStudents.find(s => s.id === selectedStudentId)?.emoji ||
                'world'
              }
            />
          )}
          <DropdownIndicator />
        </Styled.ClassRoomStudentAnswerDropdown>
      }
      dropdownContents={
        <>
          {includeAllOption && (
            <DropdownItem key="all" onClick={() => onSelect(0)}>
              <DropdownItemLabel>
                All Students ({sortedStudents.length})
              </DropdownItemLabel>
            </DropdownItem>
          )}
          {sortedStudents.map(student => (
            <DropdownItem key={student.id} onClick={() => onSelect(student.id)}>
              <ClassroomStudentIdentifier
                size={isSmall ? 24 : 40}
                name={student.name}
                emoji={student.emoji}
              />
            </DropdownItem>
          ))}
        </>
      }
    />
  );
};

export default ClassRoomStudentAnswerDropdown;
